/* ***************************************************************** */
/*                                                                   */
/* Licensed Materials - Property of Merative                         */
/*                                                                   */
/* (C) Copyright Merative Corp. 2025                                 */
/*                                                                   */
/* ***************************************************************** */
import React, { useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory } from 'react-router-dom';
import IVPickList from 'mdx-ivcomp-common-ui/dist/src/components/ivpicklist/IVPickList';
import {
    selectDrugsStatus,
    setIsPulldownOpen,
    setSearchText,
    fetchAndSaveDrugs
} from '../redux/neofax_search_redux';
import {getNeofaxPediatricsWordwheelPromise} from "../../../../../../services/neofaxPediatricsService";
import { isClickInsideIVPickList } from '../../../../utils';
import {NEOFAX_PAGE_NAME, RESULT_TYPE_NEOFAX, RESULT_TYPE_PEDIATRICS} from "../../../../common/constants";
import { trackAmplitude } from "../../../../../../utils";
import { NEOFAX_PEDIATRICS_CONTENT_SET_ID } from "../../../../../../utils/commonUtils";

export const NeofaxPedsMonographPicklist = ({
    drugTypeName,
    picklistId,
}) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const store = useStore();
    const picklistRef = useRef(null);
    const login_page = useSelector((state) => state.login_page);

    const responseLoading = useSelector((state) => selectDrugsStatus(state)(picklistId));
    const picklistState = useSelector((state) => state[NEOFAX_PAGE_NAME][picklistId]);


    useEffect(() => {
        if (performance.getEntriesByType("navigation")[0]?.type === 'reload') {
            onClearButtonClickHandler();
        }
        return () => {
            onClearButtonClickHandler();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(setIsPulldownOpen({picklist_id: picklistId, isPulldownOpen: false}));
        
        const resultType = picklistId === 'neofax_picklist' ? RESULT_TYPE_NEOFAX : RESULT_TYPE_PEDIATRICS;
        
        if (picklistState?.data_array.length === 0) {
            dispatch(fetchAndSaveDrugs({
                fetcher: () => getNeofaxPediatricsWordwheelPromise({
                    result_type: resultType, 
                    login_page
                }),
                picklist_id: picklistId,
                page_name: NEOFAX_PAGE_NAME,
                history,
                loginPage: login_page,
            }));
        }
    }, [dispatch, picklistId, login_page, history, picklistState?.data_array.length]);

    useEffect(() => {
        const handleMouseDown = (event) => {
            const scrollArea = event.target.closest('.picklist-pulldown-menu-scroll');
            if (scrollArea) {
                event.preventDefault();
            }
        };

        const handleClickOutside = (event) => {  
                      
            if (!isClickInsideIVPickList(event, picklistRef)) {
                
                dispatch(setIsPulldownOpen({
                    picklist_id: picklistId, 
                    isPulldownOpen: false
                }));

                dispatch(setSearchText({
                    value: "",
                    picklist_id: picklistId,
                    page_name: NEOFAX_PAGE_NAME
                }));

                picklistRef.current?.setState?.({
                    updatedInputValue: "",
                    currentItemIndex: -1
                });
            }
        };

        // If you use 'click', event.preventDefault() can no longer prevent the default browser behavior 
        // because it is triggered after all mouse actions have already occurred.
        const container = document.getElementById(picklistId);
        if (container) {
          container.addEventListener('mousedown', handleMouseDown, true);
        }
        document.addEventListener('click', handleClickOutside);
        
        return () => {
            if (container) {
              container.removeEventListener('mousedown', handleMouseDown, true);
            }
            document.removeEventListener('click', handleClickOutside);
        };
    }, [dispatch, picklistId]);
    
    const onInputClickHandler = useCallback(() => {
        dispatch(setIsPulldownOpen({picklist_id: picklistId, isPulldownOpen: true}));
    }, [dispatch, picklistId]);

    const onCloseButtonClickHandler = useCallback(() => {
        dispatch(setIsPulldownOpen({picklist_id: picklistId, isPulldownOpen: false}));
    }, [dispatch, picklistId]);

    const onClearButtonClickHandler = useCallback(() => {
        onCloseButtonClickHandler();
        dispatch(setSearchText({
            value: "",
            picklist_id: picklistId,
            page_name: NEOFAX_PAGE_NAME
        }));
    }, [dispatch, picklistId, onCloseButtonClickHandler]);

    const onSearchTextChange = useCallback((event) => {
        dispatch(setSearchText({
            value: event.target.value,
            picklist_id: picklistId,
            page_name: NEOFAX_PAGE_NAME
        }));
    }, [dispatch, picklistId]);

    const onPulldownSelectHandler = useCallback((index, e) => {
        const currentState = store.getState()[NEOFAX_PAGE_NAME][picklistId];
        const drugDataArray = currentState?.picklist_display_array || [];

        const element = index > -1
            ? drugDataArray[index]
            : drugDataArray.find(item => item[2]?.toUpperCase() === (e.target.value || e.target.innerText).toUpperCase());

        if (element) {
            onCloseButtonClickHandler();
            dispatch(setSearchText({
                value: "",
                picklist_id: picklistId,
                page_name: NEOFAX_PAGE_NAME
            }));
            const searchTerm = e ? (e.target.value || e.target.innerText) : element[0];
            const spellSuggestionSelection = !e;
            const monographDrugNameProperty = picklistId === "neofax_picklist" ? "neonatalMonographDrugName" : "pediatricMonographDrugName";
            const monographDrugName = element[0] || (e && (e.target.value || e.target.innerText));

            trackAmplitude(
                {
                    searchType: "Neo Peds Search",
                    actionType: "SearchAndDocRetrieval",
                    searchTerm: searchTerm,
                    spellSuggestionSelection: spellSuggestionSelection,
                    [monographDrugNameProperty]: monographDrugName,
                    DocumentName: monographDrugName
                },
                NEOFAX_PEDIATRICS_CONTENT_SET_ID
            );
            history.push({
                pathname: "/neoped/monograph",
                search: encodeURI(`?documentId=${element[1]}&drug=${encodeURIComponent(element[0])}&tab=${picklistId === "neofax_picklist" ? RESULT_TYPE_NEOFAX : RESULT_TYPE_PEDIATRICS}`)
            });
        }
    }, [store, picklistId, onCloseButtonClickHandler, dispatch, history]);

    return (
        <IVPickList
            ref={picklistRef}
            type={drugTypeName}
            autofocus={false}
            pulldown_onselect={onPulldownSelectHandler}
            clear_buttton_onclick={onClearButtonClickHandler}
            customInputClickHandler={onInputClickHandler}
            customCloseButtonClickHandler={onCloseButtonClickHandler}
            input_label="Drugs"
            input_placeholder={`Find a ${drugTypeName} Drug`}
            input_label_postfix=""
            picklist_id={picklistId}
            page_name={NEOFAX_PAGE_NAME}
            search_text_onchange={onSearchTextChange}
            iv_picklist_state={picklistState}
            redux_dispatch_function={dispatch}
            test_for_duplicates_flag={false}
            only_single_fetch_required={true}
            responseLoading={responseLoading}
            info_help_html={`
                <div>
                    <ul class="tooltip-ul">
                        <li>Click on the drug search field.</li>
                        <li>Enter the first few letters of a drug name.</li>
                        <li>Select the drug to display.</li>
                    </ul>
                </div>
            `}
        />
    );
};